import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { 
    getDailyDistributions, 
    getDailyDistributionsSummary,
    getDailyDistributionItems,
    getSalaryStatement,
    setCurrentPage,
    setManualCashAmount,
    setAttendanceStatus,
    setEditingAmount,
    saveDistributionData,
    markDistributionAsDistributed
} from '../../actions/salarySystem';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import { X } from "react-feather";

// Import the react-datepicker styles
import 'react-datepicker/dist/react-datepicker.css';

const DailyDistributionsTabExact = ({ firmId }) => {
    const dispatch = useDispatch();
    
    // Redux state
    const {
        dailyDistributions = [], 
        dailyDistributionsSummary,
        dailyDistributionItems = [],
        salaryStatement = [],
        pageTable = {
            currentPage: 1,
            itemsPerPage: 10,
            manualCashAmounts: {},
            attendanceStatuses: {},
            editingStates: {},
            editingAmount: '',
            savingPageData: false,
            saveError: null
        }
    } = useSelector(state => state.salarySystem);
    
    // Local state
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showCreateFromEarningsForm, setShowCreateFromEarningsForm] = useState(false);
    const [showCashDistributionModal, setShowCashDistributionModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedDistribution, setSelectedDistribution] = useState(null);
    const [employeeMode, setEmployeeMode] = useState('kpi');
    const [editingEmployeeId, setEditingEmployeeId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [dateRange, setDateRange] = useState(() => {
        // Check URL for date parameters first
        const urlParams = new URLSearchParams(window.location.search);
        const fromDateParam = urlParams.get('from_date');
        const toDateParam = urlParams.get('to_date');
        
        // If date parameters exist in URL, use them
        if (fromDateParam && toDateParam) {
            return [moment(fromDateParam), moment(toDateParam)];
        }
        
        // Otherwise default to today
        const today = moment();
        // Create completely fresh moment objects with clone to avoid reference issues
        return [today, today.clone()];
    });
    // Track if we're in the middle of selecting a date range
    const [isSelectingRange, setIsSelectingRange] = useState(false);
    
    // Destructure dateRange and convert moment objects to Date objects for react-datepicker
    const [startDateMoment, endDateMoment] = dateRange;
    const startDate = startDateMoment ? startDateMoment.toDate() : null;
    const endDate = endDateMoment ? endDateMoment.toDate() : null;
    
    // Ensure we always have valid moment objects for other operations
    const safeStartDate = startDateMoment && moment.isMoment(startDateMoment) ? startDateMoment : moment();
    const safeEndDate = endDateMoment && moment.isMoment(endDateMoment) ? endDateMoment : moment();
    
    // Refs for cleanup
    const isMountedRef = useRef(true);
    const abortControllerRef = useRef(null);
    const loadedFirmRef = useRef(null);

    // Redux state selectors
    const {
        currentPage,
        itemsPerPage,
        manualCashAmounts,
        attendanceStatuses,
        editingStates,
        editingAmount,
        savingPageData,
        saveError
    } = pageTable;

    // Utility functions
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US').format(amount || 0);
    };

    const formatDate = (date) => {
        return moment(date).format('DD.MM.YYYY');
    };

    const showToast = (message, type = 'info') => {
        if (isMountedRef.current) {
            toast[type](message);
        }
    };

    // Data loading function
    const loadData = useCallback(async () => {
        if (!firmId) return;
        
        try {
            // Check if component is still mounted before proceeding
            if (!isMountedRef.current) return;
            
            // Set loading state only if component is mounted
            setIsLoading(true);
            
            // Abort previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            abortControllerRef.current = new AbortController();
            
            // Use date range if available, otherwise use today
            let fromDate, toDate;
            
            if (startDateMoment && endDateMoment) {
                fromDate = startDateMoment.format('YYYY-MM-DD');
                toDate = endDateMoment.format('YYYY-MM-DD');
            } else {
                const today = moment();
                fromDate = today.format('YYYY-MM-DD');
                toDate = fromDate;
            }
            
            const params = { 
                firm_id: firmId,
                from_date: fromDate,
                to_date: toDate
            };
            
            console.log('Loading data with params:', params);
            
            await Promise.all([
                dispatch(getDailyDistributions(params)),
                dispatch(getDailyDistributionsSummary(params)),
                dispatch(getSalaryStatement(params))
            ]);
            
            // Check if component is still mounted before updating state
            if (!isMountedRef.current) return;
            
            loadedFirmRef.current = firmId;
            
            // Clear loading state only if component is still mounted
            setIsLoading(false);
        } catch (error) {
            // Check if component is still mounted before updating state
            if (!isMountedRef.current) return;
            
            // Clear loading state even on error, but only if component is still mounted
            setIsLoading(false);
            
            if (error.name !== 'AbortError') {
                console.error('Failed to load data:', error);
                showToast('Failed to load data', 'error');
            }
        }
    }, [firmId, startDate, endDate, dispatch]);
    
    // Employee filtering
    const getFilteredEmployees = useCallback(() => {
        if (!salaryStatement || !Array.isArray(salaryStatement)) return [];
        
        return salaryStatement
            .filter(employee => {
                // Filter by employee mode and visibility
                const isShown = employee.is_shown_salary_distribution_table !== false;
                const matchesMode = employeeMode === 'kpi' || 
                    (employeeMode === 'graph' && employee.base_rate_type === 'daily');
                
                return isShown && matchesMode;
            })
            .map(employee => ({
                id: employee.employee_id,
                first_name: employee.employee_name?.split(' ')[0] || '',
                last_name: employee.employee_name?.split(' ')[1] || '',
                patronymic: employee.employee_name?.split(' ')[2] || null,
                base_rate_type: employee.base_rate_type,
                is_shown_salary_distribution_table: employee.is_shown_salary_distribution_table,
                base_rate: employee.base_rate,
                previous_balance: employee.previous_balance,
                days_worked: employee.days_worked,
                earned_amount: employee.earned_amount,
                paid_amount: employee.paid_amount,
                current_balance: employee.current_balance
            }));
    }, [salaryStatement, employeeMode]);

    // Pagination
    const getPaginatedEmployees = useCallback(() => {
        const filteredEmployees = getFilteredEmployees();
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredEmployees.slice(startIndex, endIndex);
    }, [getFilteredEmployees, currentPage, itemsPerPage]);
        
        // Computed values
    const paginatedEmployees = getPaginatedEmployees();
    const totalItems = getFilteredEmployees().length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Generate date range for columns
    const getDateRangeColumns = () => {
        if (!startDateMoment || !endDateMoment) return [];
        
        const columns = [];
        const start = startDateMoment.clone();
        const end = endDateMoment.clone();
        const today = moment();
        
        // If we're viewing the current month, limit to today
        const endDate = end.clone();
        if (start.isSame(today, 'month') && end.isSame(today, 'month')) {
            // We're viewing the current month, so limit to today
            endDate.set(today);
        }
        
        let current = start;
        while (current.isSameOrBefore(endDate, 'day')) {
            columns.push({
                date: current.clone(),
                dateKey: current.format('YYYY-MM-DD'),
                displayDate: current.format('MM.YYYY')
            });
            current.add(1, 'day');
        }
        
        return columns;
    };

    const dateRangeColumns = getDateRangeColumns();
    
    // Debug logging
    console.log('Current dateRange state:', dateRange);
    console.log('startDateMoment:', startDateMoment);
    console.log('endDateMoment:', endDateMoment);
    console.log('dateRangeColumns:', dateRangeColumns);

    // Event handlers
    const handleOpenCashDistributionModal = useCallback((distribution) => {
        const date = distribution.date;
        setSelectedDate(date);
        
        // Reset page to 1 when opening modal
        dispatch(setCurrentPage(1));
        
        // Load distribution items for this date
        if (distribution.id) {
            dispatch(getDailyDistributionItems(distribution.id));
        }
        
        // Set date range to the distribution date initially
        const distributionDate = moment(date);
        setDateRange([distributionDate, distributionDate]);
        
        // Show the modal
        setShowCashDistributionModal(true);
    }, [dispatch]);

    const handleViewDistribution = useCallback(async (distribution) => {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
            setSelectedDistribution(distribution);
        }
        
        try {
            await dispatch(getDailyDistributionItems(distribution.id));
        } catch (error) {
            console.error('Failed to load distribution items:', error);
            showToast('Failed to load distribution items', 'error');
        }
    }, [dispatch]);

    const getDistributionsArray = useCallback(() => {
        if (Array.isArray(dailyDistributions)) {
            return dailyDistributions;
        } else if (typeof dailyDistributions === 'object' && dailyDistributions !== null) {
            return Object.values(dailyDistributions);
        }
        return [];
    }, [dailyDistributions]);

    const handleMarkDistributed = useCallback(async (distributionId) => {
        try {
            // First check if the distribution has any items
            const distribution = getDistributionsArray().find(d => d.id === distributionId);
            if (distribution) {
                // Try to load distribution items to check if any exist
                try {
                    await dispatch(getDailyDistributionItems(distributionId));
                    const items = dailyDistributionItems.filter(item => item.daily_distribution_id === distributionId);
                    
                    if (items.length === 0) {
                        if (isMountedRef.current) {
                            showToast(
                                'Cannot mark as distributed. No employees have been assigned to this distribution. Click "View" to add distribution items first.', 
                                'warning'
                            );
                        }
                        return;
                    }
                } catch (loadError) {
                    console.error('Failed to load distribution items:', loadError);
                }
            }
            
            await dispatch(markDistributionAsDistributed(distributionId));
            
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                showToast('Distribution marked as distributed successfully!', 'success');
                loadData();
            }
        } catch (error) {
            console.error('Failed to mark distribution as distributed:', error);
            
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                // Check if it's the "amounts do not match" error
                if (error.response?.data?.message === 'Cannot mark as distributed: amounts do not match') {
                    const data = error.response.data.data;
                    showToast(
                        `Cannot mark as distributed. You need to assign ${formatCurrency(data.difference)} сум to employees first. Click "View" to add distribution items. Total amount: ${formatCurrency(data.total_amount)}, Distributed: ${formatCurrency(data.total_distributed)}.`, 
                        'warning'
                    );
                } else {
                    showToast('Failed to mark distribution as distributed', 'error');
                }
            }
        }
    }, [dispatch, dailyDistributionItems, getDistributionsArray, loadData]);

    const handleSaveDistribution = useCallback(async () => {
        if (!firmId) return;
        
        // Check if component is still mounted before proceeding
        if (!isMountedRef.current) return;
        
        try {
            // Check if we have any manual cash amounts to save
            const hasManualCashData = Object.keys(manualCashAmounts).some(key => 
                manualCashAmounts[key] && manualCashAmounts[key] > 0
            );
            
            if (!hasManualCashData) {
                showToast('Please enter distribution amounts for at least one employee', 'warning');
                return;
            }
            
            // Prepare data for API request
            const dataToSave = {
                date: selectedDate,
                employees: Object.keys(manualCashAmounts).map(key => {
                    const amount = manualCashAmounts[key];
                    if (!amount || amount <= 0) return null;
                    
                    // Handle both single date and multiple dates format
                    if (key.includes('_')) {
                        const [employeeId, dateKey] = key.split('_');
                        return {
                            employee_id: parseInt(employeeId),
                            manual_cash_amount: amount,
                            attendance_status: attendanceStatuses[key] || null,
                            date: dateKey
                        };
                    } else {
                        return {
                            employee_id: parseInt(key),
                            manual_cash_amount: amount,
                            attendance_status: attendanceStatuses[key] || null
                        };
                    }
                }).filter(Boolean)
            };
            
            // Make API request to save distribution data
            const response = await dispatch(saveDistributionData(dataToSave));
            
            // Check if component is still mounted before updating state
            if (!isMountedRef.current) return;
            
            if (response && response.success) {
                showToast('Distribution data saved successfully!', 'success');
                
                // Refresh the data only if component is still mounted
                if (selectedDate) {
                    const params = { 
                        firm_id: firmId,
                        from_date: moment(selectedDate).format('YYYY-MM-DD'),
                        to_date: moment(selectedDate).format('YYYY-MM-DD')
                    };
                    await dispatch(getSalaryStatement(params));
                }
            } else {
                showToast('Failed to save distribution data', 'error');
            }
        } catch (error) {
            // Check if component is still mounted before updating state
            if (!isMountedRef.current) return;
            
            console.error('Failed to save distribution:', error);
            showToast('Failed to save distribution: ' + error.message, 'error');
        }
    }, [firmId, manualCashAmounts, attendanceStatuses, selectedDate, dispatch]);

    const handleDoubleClick = useCallback((employeeId) => {
        // Ensure employeeId is always a string
        const employeeIdStr = String(employeeId);
        setEditingEmployeeId(employeeIdStr);
        const currentAmount = manualCashAmounts[employeeIdStr] || '';
        dispatch(setEditingAmount(currentAmount.toString()));
    }, [manualCashAmounts, dispatch]);

    const handleEnterKey = useCallback((e) => {
        if (e.key === 'Enter' && editingEmployeeId) {
            const cleanAmount = editingAmount.replace(/,/g, '');
            const amount = parseFloat(cleanAmount) || 0;
            
            // Ensure editingEmployeeId is a string before calling includes
            const employeeIdStr = String(editingEmployeeId);
            if (employeeIdStr.includes('_')) {
                const [employeeId, dateKey] = employeeIdStr.split('_');
                dispatch(setManualCashAmount(parseInt(employeeId), amount, dateKey));
            } else {
                dispatch(setManualCashAmount(editingEmployeeId, amount));
            }
            
            setEditingEmployeeId(null);
            dispatch(setEditingAmount(''));
        }
    }, [editingEmployeeId, editingAmount, dispatch]);

    const handleAttendanceStatusChange = useCallback((employeeId, status, dateKey = null) => {
        const statusKey = dateKey ? `${employeeId}_${dateKey}` : employeeId.toString();
        dispatch(setAttendanceStatus(statusKey, status));
    }, [dispatch]);

    const handleManualCashChange = useCallback((e) => {
        // Format number as user types
        let value = e.target.value.replace(/[^0-9]/g, '');
        if (value) {
            value = parseInt(value).toLocaleString();
        }
        dispatch(setEditingAmount(value));
    }, [dispatch]);

    const handleBlur = useCallback(() => {
        if (editingEmployeeId && editingAmount) {
            const cleanAmount = editingAmount.replace(/,/g, '');
            const amount = parseFloat(cleanAmount) || 0;
            
            // Ensure editingEmployeeId is a string before calling includes
            const employeeIdStr = String(editingEmployeeId);
            if (employeeIdStr.includes('_')) {
                const [employeeId, dateKey] = employeeIdStr.split('_');
                dispatch(setManualCashAmount(parseInt(employeeId), amount, dateKey));
            } else {
                dispatch(setManualCashAmount(editingEmployeeId, amount));
            }
            
            setEditingEmployeeId(null);
            dispatch(setEditingAmount(''));
        }
    }, [editingEmployeeId, editingAmount, dispatch]);

    // Initialize date range on mount and ensure it's always valid
    useEffect(() => {
        if (!dateRange || dateRange.length !== 2) {
            const today = moment();
            setDateRange([today, today.clone()]);
        }
    }, []);
    
    // Additional safety check - ensure dateRange is always valid
    useEffect(() => {
        if (dateRange && dateRange.length === 2) {
            const [start, end] = dateRange;
            // Simple validation to avoid excessive checks
            if (!start || !end) {
                const today = moment();
                setDateRange([today, today.clone()]);
            }
        }
    }, [dateRange]);

    // Reload data when date range changes
    useEffect(() => {
        // Create a flag to track if the effect is still relevant
        let isEffectActive = true;
        
        const reloadData = async () => {
            // Only proceed if component is mounted and effect is still active
            if (!isMountedRef.current || !isEffectActive) return;
            
            // Skip data loading if we're in the middle of selecting a date range
            if (isSelectingRange) {
                console.log('Skipping data loading while selecting date range');
                return;
            }
            
            if (firmId && startDateMoment && endDateMoment) {
                // Only reload if we have valid dates
                if (startDateMoment.isValid() && endDateMoment.isValid()) {
                    // Call loadData directly without including it in dependencies
                    if (!firmId) return;
                    
                    try {
                        // Check if component is still mounted before proceeding
                        if (!isMountedRef.current) return;
                        
                        // Set loading state only if component is mounted
                        setIsLoading(true);
                        
                        // Abort previous request
                        if (abortControllerRef.current) {
                            abortControllerRef.current.abort();
                        }
                        
                        abortControllerRef.current = new AbortController();
                        
                        // Use date range if available, otherwise use today
                        let fromDate, toDate;
                        
                        if (startDateMoment && endDateMoment) {
                            fromDate = startDateMoment.format('YYYY-MM-DD');
                            toDate = endDateMoment.format('YYYY-MM-DD');
                        } else {
                            const today = moment();
                            fromDate = today.format('YYYY-MM-DD');
                            toDate = fromDate;
                        }
                        
                        const params = { 
                            firm_id: firmId,
                            from_date: fromDate,
                            to_date: toDate
                        };
                        
                        console.log('Loading data with params:', params);
                        
                        await Promise.all([
                            dispatch(getDailyDistributions(params)),
                            dispatch(getDailyDistributionsSummary(params)),
                            dispatch(getSalaryStatement(params))
                        ]);
                        
                        // Check if component is still mounted before updating state
                        if (!isMountedRef.current) return;
                        
                        loadedFirmRef.current = firmId;
                        
                        // Clear loading state only if component is still mounted
                        setIsLoading(false);
                    } catch (error) {
                        // Check if component is still mounted before updating state
                        if (!isMountedRef.current) return;
                        
                        // Clear loading state even on error, but only if component is still mounted
                        setIsLoading(false);
                        
                        if (error.name !== 'AbortError') {
                            console.error('Failed to load data:', error);
                            showToast('Failed to load data', 'error');
                        }
                    }
                }
            }
        };
        
        reloadData();
        
        // Cleanup function to prevent state updates after unmount
        return () => {
            isEffectActive = false;
        };
    }, [startDateMoment?.format('YYYY-MM-DD'), endDateMoment?.format('YYYY-MM-DD'), firmId, isSelectingRange, dispatch]);

    // Cleanup - prevent memory leaks and cancel all subscriptions
    useEffect(() => {
        // Set mounted flag
        isMountedRef.current = true;
        
        return () => {
            // Set unmounted flag to prevent state updates
            isMountedRef.current = false;
            loadedFirmRef.current = null;
            
            // Cancel any in-flight requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, []);

    // Load initial data only once when component mounts
    useEffect(() => {
        // Only load data once when component mounts with valid firmId
        if (firmId && startDateMoment && endDateMoment) {
            const loadInitialData = async () => {
                try {
                    // Check if component is still mounted
                    if (!isMountedRef.current) return;
                    
                    const formatted_start_date = startDateMoment.format('YYYY-MM-DD');
                    const formatted_end_date = endDateMoment.format('YYYY-MM-DD');
                    
                    const params = { 
                        firm_id: firmId,
                        from_date: formatted_start_date,
                        to_date: formatted_end_date
                    };
                    
                    await dispatch(getSalaryStatement(params));
                } catch (error) {
                    // Only log error if component is still mounted
                    if (isMountedRef.current) {
                        console.error('Failed to load initial data:', error);
                    }
                }
            };
            
            loadInitialData();
        }
    }, [firmId]); // Only depend on firmId to load once

    // Reset page when employee mode changes
    useEffect(() => {
        dispatch(setCurrentPage(1));
    }, [employeeMode, dispatch]);

    // Event handlers

    const handleExportPDF = () => {
        console.log('Export PDF');
        // Implement PDF export logic
    };

    const handlePrint = () => {
        console.log('Print');
        // Implement print logic
    };

    const handlePageChange = (page) => {
        // Dispatch action to update the current page
        dispatch(setCurrentPage(page));
    };

    const handleItemsPerPageChange = (itemsPerPage) => {
        // This will be handled by the custom hook
        console.log('Items per page change:', itemsPerPage);
    };

    const handleDateRangeChange = (dates) => {
        try {
            console.log('Date range change:', dates);
            // For selectsRange, react-datepicker provides [startDate, endDate]
            const [start, end] = dates;
            
            if (start === null) {
                // Reset to today if clearing selection
                const today = moment();
                setIsSelectingRange(false);
                setDateRange([today, today.clone()]);
            } else if (end !== null) {
                // Both dates are selected, set the full range and trigger API call
                const startMoment = moment(start);
                const endMoment = moment(end);
                console.log('Both dates selected, setting range and loading data');
                setIsSelectingRange(false);
                setDateRange([startMoment, endMoment]);
            } else {
                // Only start date is selected - set isSelectingRange to true to prevent data loading
                console.log('Start date selected, waiting for end date...');
                setIsSelectingRange(true);
                
                // Update UI for first date selection
                const startMoment = moment(start);
                // Use the existing endDate value that we destructured from the hook
                setDateRange([startMoment, endDate]);
            }
        } catch (error) {
            console.error('Error handling date range change:', error);
            // Fallback to current date
            const today = moment();
            setIsSelectingRange(false);
            setDateRange([today, today.clone()]);
        }
    };

    const handleDateRangeReset = () => {
        try {
            console.log('Date range reset');
            const today = moment();
            setIsSelectingRange(false);
            setDateRange([today, today.clone()]);
        } catch (error) {
            console.error('Error resetting date range:', error);
        }
    };

    if (!firmId) {
        return <div className="content-card">Please select a firm to view daily distributions.</div>;
    }

    // Add custom styles for the date picker
    const datePickerStyles = `
        .react-datepicker-wrapper {
            width: 100%;
        }
        .react-datepicker__input-container {
            width: 100%;
        }
        .react-datepicker__input-container input {
            width: 100%;
            height: 38px;
            padding: 0.375rem 0.75rem;
            font-size: 1rem;
            border: 1px solid #ced4da;
            border-radius: 0.25rem;
        }
        .input-group {
            position: relative;
            display: flex;
            flex-wrap: wrap;
            align-items: stretch;
            width: 100%;
        }
        .input-group-text {
            display: flex;
            align-items: center;
            padding: 0.375rem 0.75rem;
            font-size: 1rem;
            font-weight: 400;
            line-height: 1.5;
            color: #212529;
            text-align: center;
            white-space: nowrap;
            background-color: #e9ecef;
            border: 1px solid #ced4da;
            border-radius: 0 0.25rem 0.25rem 0;
            cursor: pointer;
        }
        .cursor-pointer {
            cursor: pointer;
        }
        .border-0 {
            border: 0 !important;
        }
        .pe-0 {
            padding-right: 0 !important;
        }
        .w-100 {
            width: 100% !important;
        }
        .focus-outline-0:focus {
            outline: none !important;
            box-shadow: none !important;
        }
    `;

    return (
        <div className="daily-distributions-tab">
            <style>{datePickerStyles}</style>
            {/* Summary Cards */}
            {dailyDistributionsSummary && (
                <div className="summary-grid">
                    <div className="summary-card">
                        <h4>Total Distributions</h4>
                        <p className="value">{dailyDistributionsSummary.total_count || 0}</p>
                        <p className="subtitle">distributions</p>
                    </div>
                    <div className="summary-card">
                        <h4>Total Amount</h4>
                        <p className="value">{formatCurrency(dailyDistributionsSummary.total_amount || 0)}</p>
                        <p className="subtitle">сум</p>
                    </div>
                    <div className="summary-card">
                        <h4>Distributed</h4>
                        <p className="value">{dailyDistributionsSummary.distributed_count || 0}</p>
                        <p className="subtitle">completed</p>
                    </div>
                    <div className="summary-card">
                        <h4>Pending</h4>
                        <p className="value">{dailyDistributionsSummary.pending_count || 0}</p>
                        <p className="subtitle">pending</p>
                    </div>
                </div>
            )}

            {/* Date Filter */}
            <div className="content-card">
                <h3>Date Filter</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div style={{ display: 'flex', gap: '15px' }}>
                        <div style={{ flex: '1' }}>
                            <div style={{ marginBottom: '5px' }}>From Date</div>
                            <input
                                type="date"
                                value={startDateMoment ? startDateMoment.format('YYYY-MM-DD') : ''}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        const newStartDate = moment(e.target.value);
                                        setIsSelectingRange(false);
                                        setDateRange([newStartDate, endDateMoment || newStartDate.clone()]);
                                    }
                                }}
                                className="form-control"
                                style={{ width: '100%', height: '38px' }}
                            />
                        </div>
                        <div style={{ flex: '1' }}>
                            <div style={{ marginBottom: '5px' }}>To Date</div>
                            <input
                                type="date"
                                value={endDateMoment ? endDateMoment.format('YYYY-MM-DD') : ''}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        const newEndDate = moment(e.target.value);
                                        setIsSelectingRange(false);
                                        setDateRange([startDateMoment || newEndDate.clone(), newEndDate]);
                                    }
                                }}
                                className="form-control"
                                style={{ width: '100%', height: '38px' }}
                            />
                    </div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                            <button 
                                className="btn btn-primary"
                                style={{ height: '38px' }}
                                onClick={() => {
                                    setIsSelectingRange(false);
                                    loadData();
                                }}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Loading...' : 'Apply Filter'}
                            </button>
                            <button 
                                className="btn btn-outline-secondary"
                                style={{ height: '38px' }}
                                onClick={() => {
                                    const today = moment();
                                    setIsSelectingRange(false);
                                    setDateRange([today.clone(), today.clone()]);
                                }}
                            >
                                Clear Filter
                            </button>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                                const today = moment();
                                setIsSelectingRange(false);
                                setDateRange([today.clone(), today.clone()]);
                            }}
                        >
                            Today
                        </button>
                        <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                                const today = moment();
                                const yesterday = today.clone().subtract(1, 'days');
                                setIsSelectingRange(false);
                                setDateRange([yesterday, yesterday]);
                            }}
                        >
                            Yesterday
                        </button>
                        <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                                const today = moment();
                                const startOfWeek = today.clone().startOf('week');
                                const endOfWeek = today.clone().endOf('week');
                                setIsSelectingRange(false);
                                setDateRange([startOfWeek, endOfWeek]);
                            }}
                        >
                            This Week
                        </button>
                        <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                                const today = moment();
                                const startOfMonth = today.clone().startOf('month');
                                const endOfMonth = today.clone().endOf('month');
                                setIsSelectingRange(false);
                                setDateRange([startOfMonth, endOfMonth]);
                            }}
                        >
                            This Month
                        </button>
                    </div>
                </div>
            </div>

            {/* Daily Distributions Table */}
            <div className="content-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3>Daily Distributions List</h3>
                </div>
                {isLoading || savingPageData ? (
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                        <p>Loading daily distributions...</p>
                    </div>
                ) : dailyDistributions.length > 0 ? (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Date</th>
                                <th>Total Amount</th>
                                <th>Description</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dailyDistributions.map((distribution) => (
                                <tr key={distribution.id}>
                                    <td>{distribution.id}</td>
                                    <td>{formatDate(distribution.date)}</td>
                                    <td>{formatCurrency(distribution.total_amount)}</td>
                                    <td>{distribution.description || '-'}</td>
                                    <td>
                                        <span className={`status-badge ${
                                            distribution.is_distributed ? 'status-distributed' : 'status-pending'
                                        }`}>
                                            {distribution.is_distributed ? 'Distributed' : 'Pending'}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            <button 
                                                className="btn btn-primary"
                                                onClick={() => handleViewDistribution(distribution)}
                                                style={{ padding: '5px 10px', fontSize: '12px' }}
                                            >
                                                View
                                            </button>
                                            <button 
                                                className="btn btn-info"
                                                onClick={() => handleOpenCashDistributionModal(distribution)}
                                                style={{ padding: '5px 10px', fontSize: '12px' }}
                                            >
                                                Cash Statement
                                            </button>
                                            {!distribution.is_distributed && (
                                                <button 
                                                    className="btn btn-warning"
                                                    onClick={() => handleMarkDistributed(distribution.id)}
                                                    style={{ padding: '5px 10px', fontSize: '12px' }}
                                                >
                                                    Mark Distributed
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                        <p>No daily distributions found for this firm.</p>
                        <p>Create a distribution from production earnings or manually.</p>
                    </div>
                )}
            </div>

            {/* Distribution Details Modal */}
            {selectedDistribution && (
                <div className="content-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3>Distribution Details - {formatDate(selectedDistribution.date)}</h3>
                        <button 
                            className="btn btn-primary"
                            onClick={() => setSelectedDistribution(null)}
                        >
                            Close
                        </button>
                    </div>
                    
                    <div style={{ marginBottom: '20px' }}>
                        <p><strong>Total Amount:</strong> {formatCurrency(selectedDistribution.total_amount)}</p>
                        <p><strong>Description:</strong> {selectedDistribution.description || 'No description'}</p>
                        <p><strong>Status:</strong> 
                            <span className={`status-badge ${
                                selectedDistribution.is_distributed ? 'status-distributed' : 'status-pending'
                            }`} style={{ marginLeft: '10px' }}>
                                {selectedDistribution.is_distributed ? 'Distributed' : 'Pending'}
                            </span>
                        </p>
                    </div>

                    {/* Distribution Items */}
                    <h4>Employee Distributions</h4>
                    {dailyDistributionItems.length > 0 ? (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Amount</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dailyDistributionItems.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.employee?.first_name} {item.employee?.last_name}</td>
                                        <td>{formatCurrency(item.distributed_amount)}</td>
                                        <td>{item.notes || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#7f8c8d' }}>
                            <p>No employee distributions found for this date.</p>
                            <p>Use the bulk create feature to distribute amounts to employees.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Cash Distribution Modal */}
            {showCashDistributionModal && selectedDate && (
                <div 
                    className="modal-overlay" 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}
                >
                    <div 
                        className="modal-content" 
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            width: '95%',
                            maxWidth: '1400px',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative'
                        }}
                    >
                        {/* Modal Header */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            padding: '15px 20px',
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'white',
                            zIndex: 10,
                            borderBottom: '1px solid #e9ecef'
                        }}>
                            <h2 style={{ margin: 0, color: '#2c3e50' }}>
                                Cash Distribution Statement - {formatDate(selectedDate)}
                            </h2>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <button
                                    onClick={handleSaveDistribution}
                                    style={{
                                        padding: '8px 20px',
                                        border: 'none',
                                        borderRadius: '6px',
                                        backgroundColor: '#27ae60',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    СОХРАНИТЬ
                                </button>
                                <button 
                                    onClick={() => setShowCashDistributionModal(false)}
                                    style={{
                                        padding: '8px',
                                        border: 'none',
                                        borderRadius: '6px',
                                        backgroundColor: '#e74c3c',
                                        color: 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <span style={{ fontSize: '18px' }}>&times;</span>
                                </button>
                            </div>
                        </div>

                        {/* Modal Content */}
                        <div style={{ 
                            flex: 1, 
                            padding: '30px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}>
                            {/* Error Message */}
                            {saveError && (
                                <div style={{
                                    backgroundColor: '#f8d7da',
                                    color: '#721c24',
                                    padding: '12px',
                                    borderRadius: '4px',
                                    marginBottom: '20px',
                                    border: '1px solid #f5c6cb'
                                }}>
                                    {saveError}
                                </div>
                            )}

                            {/* Date Range Selector */}
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ margin: '0 0 10px 0', color: '#2c3e50' }}>Date Range</h4>
                                <div className="input-group mb-3" style={{ maxWidth: '300px' }}>
                                    <DatePicker
                                        wrapperClassName={'form-control'}
                                        className={'border-0 pe-0 w-100 focus-outline-0'}
                                        placeholderText={"Dan-Gacha"}
                                        selectsRange={true}
                                        startDate={startDate}
                                        endDate={endDate}
                                        maxDate={moment()}
                                        shouldCloseOnSelect={false}
                                        isClearable={false}
                                        monthsShown={2}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        onChange={(update) => {
                                            console.log('DatePicker onChange:', update);
                                            // Convert Date objects to moment objects
                                            const [newStartDate, newEndDate] = update;
                                            const newStartMoment = newStartDate ? moment(newStartDate) : null;
                                            const newEndMoment = newEndDate ? moment(newEndDate) : null;
                                            
                                            console.log('New dates:', { newStartMoment, newEndMoment });
                                            
                                            // Update state with moment objects
                                            setDateRange([newStartMoment, newEndMoment]);
                                            
                                            // If both dates are selected, fetch data
                                            if (newStartDate && newEndDate && selectedDate) {
                                                const params = { 
                                                    firm_id: firmId,
                                                    from_date: moment(newStartDate).format('YYYY-MM-DD'),
                                                    to_date: moment(newEndDate).format('YYYY-MM-DD')
                                                };
                                                console.log('Fetching data with params:', params);
                                                dispatch(getSalaryStatement(params));
                                            }
                                        }}
                                    />
                                    <span 
                                        className="input-group-text cursor-pointer" 
                                        onClick={() => {
                                            console.log('Clearing date range');
                                            // Reset to null values
                                            setDateRange([null, null]);
                                            if (selectedDate) {
                                                const distributionDate = moment(selectedDate);
                                                const params = { 
                                                    firm_id: firmId,
                                                    from_date: distributionDate.format('YYYY-MM-DD'),
                                                    to_date: distributionDate.format('YYYY-MM-DD')
                                                };
                                                dispatch(getSalaryStatement(params));
                                            }
                                        }}
                                    >
                                        <X/>
                                    </span>
                                </div>
                            </div>

                            {/* Title and Mode Selection */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', marginTop: '30px' }}>
                                <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '20px' }}>
                                    Зарплатная ведомость от {selectedDate ? moment(selectedDate).format('MMMM YYYY') : 'текущий месяц'} г.
                                </h3>
                                
                                {/* Mode Selection Buttons */}
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        onClick={() => setEmployeeMode('graph')}
                                        style={{
                                            padding: '8px 16px',
                                            border: 'none',
                                            borderRadius: '6px',
                                            backgroundColor: employeeMode === 'graph' ? '#3498db' : '#e9ecef',
                                            color: employeeMode === 'graph' ? 'white' : '#495057',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        📊 ГРАФИК
                                    </button>
                                    <button
                                        onClick={() => setEmployeeMode('kpi')}
                                        style={{
                                            padding: '8px 16px',
                                            border: 'none',
                                            borderRadius: '6px',
                                            backgroundColor: employeeMode === 'kpi' ? '#3498db' : '#e9ecef',
                                            color: employeeMode === 'kpi' ? 'white' : '#495057',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        🔧 ОБЪЕМНЫЙ
                                    </button>
                                </div>
                            </div>

                            {/* Employee Table */}
                            <div style={{ marginBottom: '20px' }}>
                                <h3 style={{ margin: '0 0 20px 0', color: '#2c3e50' }}>Employee Salary Statement</h3>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', minWidth: `${1000 + (dateRangeColumns.length * 120)}px` }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Сотрудник</th>
                                            <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Оклад, сум</th>
                                            <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Остаток пред</th>
                                            <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Отработано</th>
                                            <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Заработано</th>
                                            <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Оплачено</th>
                                            <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Остаток тек</th>
                                            {dateRangeColumns.map((column, index) => (
                                                <th key={index} style={{ padding: '12px', textAlign: 'center', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                        <div style={{ fontSize: '12px', marginBottom: '4px' }}>{column.displayDate}</div>
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <div style={{ fontSize: '10px', minWidth: '60px', textAlign: 'center' }}>Manual Cash</div>
                                                            <div style={{ fontSize: '10px', minWidth: '40px', textAlign: 'center' }}>Att.</div>
                                                        </div>
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedEmployees.map(employee => (
                                            <tr key={employee.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                                                <td style={{ padding: '12px', textAlign: 'left', borderRight: '1px solid #dee2e6' }}>
                                                    {employee.first_name} {employee.last_name} {employee.patronymic || ''}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                                    {formatCurrency(employee.base_rate || 0)}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                                    {formatCurrency(employee.previous_balance || 0)}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                                    {employee.days_worked || 0}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                                    {formatCurrency(employee.earned_amount || 0)}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                                    {formatCurrency(employee.paid_amount || 0)}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                                    {formatCurrency(employee.current_balance || 0)}
                                                </td>
                                                {dateRangeColumns.map((column, index) => (
                                                    <td key={index} style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #dee2e6' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                            <div 
                                                                style={{ 
                                                                    width: '60px',
                                                                    position: 'relative',
                                                                    border: '1px solid #ced4da',
                                                                    borderRadius: '4px',
                                                                    padding: '4px 6px',
                                                                    textAlign: 'right',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px'
                                                                }}
                                                                onDoubleClick={() => handleDoubleClick(`${employee.id}_${column.dateKey}`)}
                                                            >
                                                                {editingEmployeeId === `${employee.id}_${column.dateKey}` ? (
                                                                    <input
                                                                        type="text"
                                                                        value={editingAmount}
                                                                        onChange={handleManualCashChange}
                                                                        onKeyDown={handleEnterKey}
                                                                        onBlur={handleBlur}
                                                                        autoFocus
                                                                        style={{
                                                                            width: '100%',
                                                                            border: 'none',
                                                                            outline: 'none',
                                                                            textAlign: 'right',
                                                                            padding: '0',
                                                                            fontSize: '12px'
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    formatCurrency(manualCashAmounts[`${employee.id}_${column.dateKey}`] || 0)
                                                                )}
                                                            </div>
                                                            <select
                                                                value={attendanceStatuses[`${employee.id}_${column.dateKey}`] || ''}
                                                                onChange={(e) => handleAttendanceStatusChange(employee.id, e.target.value, column.dateKey)}
                                                                style={{ 
                                                                    width: '40px',
                                                                    padding: '4px 2px',
                                                                    borderRadius: '4px',
                                                                    border: '1px solid #ced4da',
                                                                    fontSize: '12px'
                                                                }}
                                                            >
                                                                <option value="">-</option>
                                                                <option value="present">✓</option>
                                                                <option value="absent">✗</option>
                                                                <option value="half_day">½</option>
                                                            </select>
                                                        </div>
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                            </div>

                            {/* Pagination */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
                                <div>
                                    <span>Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} employees</span>
                                </div>
                                <div style={{ display: 'flex', gap: '5px' }}>
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        style={{
                                            padding: '5px 10px',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '4px',
                                            backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                                            color: currentPage === 1 ? '#6c757d' : '#007bff',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        Previous
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            onClick={() => handlePageChange(page)}
                                            style={{
                                                padding: '5px 10px',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '4px',
                                                backgroundColor: page === currentPage ? '#007bff' : 'white',
                                                color: page === currentPage ? 'white' : '#007bff',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            padding: '5px 10px',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '4px',
                                            backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
                                            color: currentPage === totalPages ? '#6c757d' : '#007bff',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div style={{
                            padding: '20px',
                            borderTop: '1px solid #dee2e6',
                            backgroundColor: '#f8f9fa',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 10
                        }}>
                            <div>
                                <button
                                    onClick={handleExportPDF}
                                    style={{
                                        padding: '8px 16px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        backgroundColor: 'white',
                                        color: '#495057',
                                        cursor: 'pointer',
                                        marginRight: '10px'
                                    }}
                                >
                                    Export PDF
                                </button>
                                <button
                                    onClick={handlePrint}
                                    style={{
                                        padding: '8px 16px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        backgroundColor: 'white',
                                        color: '#495057',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Print
                                </button>
                            </div>
                            <div>
                                <button
                                    onClick={() => setShowCashDistributionModal(false)}
                                    style={{
                                        padding: '8px 16px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        backgroundColor: 'white',
                                        color: '#495057',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default DailyDistributionsTabExact;